import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Flex, Text, Select, Button, Textarea, Spinner, Collapse, Alert, AlertIcon, Tooltip,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiMessageCircle } from 'react-icons/fi';
import { StatusPill } from 'components/monitoring/ai/common';
import { errorMessage } from 'api/administrador/documentRequests';
import {
  FINDING_CATEGORY, FINDING_IMPACT, FINDING_PRIORITY, FINDING_STATUS, PROVIDER_LABEL, TOOL_LABEL, RATING,
  listFindings, updateFinding,
} from 'api/administrador/ventanillaSimulator';

/**
 * Pestaña de hallazgos del simulador: lo que reportó el equipo, para clasificarlo, corregirlo y
 * volver a probar. Cada hallazgo abre la conversación en el punto reportado.
 */

const date = (d) => new Date(d).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function FindingRow({ finding, scenarios, onOpen, onChanged }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(finding.note || '');
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const st = FINDING_STATUS[finding.status];
  const ctx = finding.context || {};

  useEffect(() => { setNote(finding.note || ''); }, [finding.note]);

  const save = async (key, body) => {
    setSaving(key);
    setError('');
    try {
      onChanged(await updateFinding(finding.id, body));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving('');
    }
  };

  const scenario = scenarios.find((s) => s.value === finding.scenario)?.label || finding.scenarioLabel;

  return (
    <Box borderTop="1px solid" borderColor="gray.100" py={3}>
      <Flex gap={3} align="flex-start" wrap={{ base: 'wrap', md: 'nowrap' }}>
        <Flex
          as="button" type="button" flex="1" minW={0} gap={2} align="flex-start" textAlign="left"
          onClick={() => setOpen((o) => !o)} aria-expanded={open}
        >
          <Box as={open ? FiChevronDown : FiChevronRight} color="gray.400" mt={1} flexShrink={0} />
          <Box minW={0}>
            <Flex gap={2} align="center" wrap="wrap" mb={1}>
              <Text fontSize="12px" color="gray.500" fontFamily="mono">#{finding.id}</Text>
              {st && <StatusPill label={st.label} tone={st.tone} size="10px" />}
              {finding.impact && <StatusPill label={FINDING_IMPACT[finding.impact]?.label} tone={FINDING_IMPACT[finding.impact]?.tone} size="10px" />}
              <Text fontSize="12px" color="gray.600">
                {finding.source === 'calificacion'
                  ? `Calificación: ${RATING[ctx.rating]?.label || '—'}`
                  : FINDING_CATEGORY[finding.category]}
              </Text>
            </Flex>
            <Text fontSize="14px" noOfLines={open ? undefined : 2}>{finding.description}</Text>
            <Text fontSize="12px" color="gray.500" mt={1}>
              {[scenario, finding.createdBy, date(finding.createdAt)].filter(Boolean).join(' · ')}
            </Text>
          </Box>
        </Flex>
        <Flex gap={2} align="center" flexShrink={0}>
          <Select
            size="sm" w="150px" borderRadius="8px" aria-label={`Prioridad del hallazgo ${finding.id}`}
            value={finding.priority || ''} isDisabled={saving === 'prioridad'}
            onChange={(e) => save('prioridad', { priority: e.target.value || null })}
          >
            <option value="">Sin prioridad</option>
            {Object.entries(FINDING_PRIORITY).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
          </Select>
          <Select
            size="sm" w="170px" borderRadius="8px" aria-label={`Estado del hallazgo ${finding.id}`}
            value={finding.status} isDisabled={saving === 'estado'}
            onChange={(e) => save('estado', { status: e.target.value })}
          >
            {Object.entries(FINDING_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </Select>
          <Tooltip label="Abrir la conversación en este punto" hasArrow>
            <Button
              size="sm" variant="outline" borderRadius="8px" leftIcon={<FiMessageCircle />}
              onClick={() => onOpen(finding.conversationId, finding.messageId)}
            >
              {`#${finding.conversationId}`}
            </Button>
          </Tooltip>
        </Flex>
      </Flex>
      <Collapse in={open} animateOpacity>
        <Box ml={6} mt={3} fontSize="13px">
          {finding.expected && (
            <Box mb={3}>
              <Text fontSize="11px" fontWeight="700" color="gray.500">Lo esperado</Text>
              <Text whiteSpace="pre-wrap">{finding.expected}</Text>
            </Box>
          )}
          <Text fontSize="12px" color="gray.500" mb={3} fontFamily="mono">
            {[
              PROVIDER_LABEL[ctx.provider] || ctx.provider,
              ctx.model,
              ctx.guion && `guion ${ctx.guion}`,
              (ctx.tools || []).length ? `herramientas: ${ctx.tools.map((t) => `${TOOL_LABEL[t.name] || t.name}${t.ok ? '' : ' (falló)'}`).join(', ')}` : 'sin herramientas',
            ].filter(Boolean).join(' · ')}
          </Text>
          <Text as="label" htmlFor={`finding-note-${finding.id}`} fontSize="11px" fontWeight="700" color="gray.500">Nota de la corrección</Text>
          <Textarea
            id={`finding-note-${finding.id}`} rows={2} fontSize="13px" borderRadius="8px" mt={1}
            placeholder="Qué se cambió y en qué versión, o por qué se descarta"
            value={note} onChange={(e) => setNote(e.target.value)}
          />
          <Flex justify="flex-end" mt={2} gap={2} align="center">
            {finding.updatedBy && <Text fontSize="11px" color="gray.400">{`Último cambio: ${finding.updatedBy}`}</Text>}
            <Button size="xs" variant="outline" onClick={() => save('nota', { note })} isLoading={saving === 'nota'} isDisabled={note === (finding.note || '')}>
              Guardar nota
            </Button>
          </Flex>
        </Box>
      </Collapse>
      {error && <Text fontSize="12px" color="red.600" mt={1} ml={6}>{error}</Text>}
    </Box>
  );
}

export default function FindingsTab({ scenarios, onOpen, onCounts, refreshKey }) {
  const [status, setStatus] = useState('abiertos');
  const [priority, setPriority] = useState('');
  const [scenario, setScenario] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await listFindings({ status: status || undefined, priority: priority || undefined, scenario: scenario || undefined, page });
      setResult(r);
      onCounts?.(r.meta.counts);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [status, priority, scenario, page, onCounts]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const filter = (setter) => (e) => { setter(e.target.value); setPage(1); };
  const replace = (updated) => {
    setResult((r) => ({ ...r, data: r.data.map((f) => (f.id === updated.id ? updated : f)) }));
    load();
  };

  const counts = result?.meta?.counts || {};
  const meta = result?.meta;

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4}>
      <Flex gap={3} wrap="wrap" mb={3} align="center">
        <Select size="sm" w="210px" borderRadius="8px" aria-label="Estado" value={status} onChange={filter(setStatus)}>
          <option value="abiertos">{`Abiertos (${(counts.nuevo || 0) + (counts.en_correccion || 0) + (counts.listo_reprobar || 0)})`}</option>
          {Object.entries(FINDING_STATUS).map(([k, s]) => <option key={k} value={k}>{`${s.label} (${counts[k] || 0})`}</option>)}
          <option value="">Todos</option>
        </Select>
        <Select size="sm" w="170px" borderRadius="8px" aria-label="Prioridad" value={priority} onChange={filter(setPriority)}>
          <option value="">Cualquier prioridad</option>
          <option value="sin">Sin prioridad</option>
          {Object.entries(FINDING_PRIORITY).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
        </Select>
        <Select size="sm" w="250px" borderRadius="8px" aria-label="Escenario" value={scenario} onChange={filter(setScenario)}>
          <option value="">Todos los escenarios</option>
          {scenarios.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        {loading && <Spinner size="sm" color="#574feb" />}
      </Flex>

      {error && <Alert status="error" borderRadius="10px" mb={3} fontSize="sm"><AlertIcon />{error}</Alert>}

      {result && result.data.length === 0 && !loading && (
        <Text fontSize="14px" color="gray.500" py={6} textAlign="center">
          No hay hallazgos con estos filtros. Se crean con la bandera de cada respuesta o al calificar una conversación como parcial o incorrecta.
        </Text>
      )}
      {result?.data.map((f) => (
        <FindingRow key={f.id} finding={f} scenarios={scenarios} onOpen={onOpen} onChanged={replace} />
      ))}

      {meta && meta.pageCount > 1 && (
        <Flex justify="flex-end" align="center" gap={3} mt={3}>
          <Text fontSize="12px" color="gray.500">{`Página ${meta.page} de ${meta.pageCount} · ${meta.total} hallazgos`}</Text>
          <Button size="xs" variant="outline" onClick={() => setPage((p) => p - 1)} isDisabled={meta.page <= 1}>Anterior</Button>
          <Button size="xs" variant="outline" onClick={() => setPage((p) => p + 1)} isDisabled={meta.page >= meta.pageCount}>Siguiente</Button>
        </Flex>
      )}
    </Box>
  );
}
